import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { getAuthHeaders, logout } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  BookOpen,
  Search,
  Plus,
  LogOut,
  User,
  Trash2,
  ExternalLink,
  Calendar,
  Crown,
  LayoutDashboard,
  Settings,
  ChevronRight,
  Menu,
  X,
  FileText,
  Link as LinkIcon,
  Upload,
  Mic,
  Sparkles,
  Youtube,
  BookMarked,
  Wand2,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { AnalysisProgress } from '../components/AnalysisProgress';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PAID_TIERS = [
  'founder_monthly', 'pastor_pro', 'founder_lifetime', 'seminary_annual',
  // legacy
  'ministry_plus', 'church_team', 'pro',
];

export default function DashboardPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [sermons, setSermons] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [transcriptText, setTranscriptText] = useState('');
  const [videoTitle, setVideoTitle] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [audioFile, setAudioFile] = useState(null);
  const [audioTitle, setAudioTitle] = useState('');
  const [audioUrl, setAudioUrl] = useState('');
  const [audioUrlTitle, setAudioUrlTitle] = useState('');
  const [jobStatus, setJobStatus] = useState(null);
  const [showProgress, setShowProgress] = useState(false);
  const [syncProgress, setSyncProgress] = useState(null); // { label, percent } for sync path
  const [summaryUrl, setSummaryUrl] = useState('');
  const [isSummarizing, setIsSummarizing] = useState(false);
  const [searchHistory, setSearchHistory] = useState([]);
  const syncProgressRef = useRef(null);
  const fileInputRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [inputMode, setInputMode] = useState('url');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sermonToDelete, setSermonToDelete] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [youtubeError, setYoutubeError] = useState(null);
  const [usage, setUsage] = useState(null);
  const [limitHit, setLimitHit] = useState(null);

  useEffect(() => {
    loadUserAndSermons();
    
    // Check for payment success
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const loadUserAndSermons = async () => {
    try {
      // Load user
      const userResponse = await fetch(`${API}/auth/me`, { headers: { ...getAuthHeaders() } });
      if (!userResponse.ok) {
        navigate('/login');
        return;
      }
      const userData = await userResponse.json();
      setUser(userData);

      // Load search history for this user
      const historyKey = `ss_search_history_${userData.user_id}`;
      try {
        const stored = localStorage.getItem(historyKey);
        if (stored) setSearchHistory(JSON.parse(stored));
      } catch {}

      // Load sermons and usage in parallel
      const [sermonsResponse, usageResponse] = await Promise.all([
        fetch(`${API}/sermons`, { headers: { ...getAuthHeaders() } }),
        fetch(`${API}/usage`, { headers: { ...getAuthHeaders() } }),
      ]);
      if (sermonsResponse.ok) {
        const sermonsData = await sermonsResponse.json();
        setSermons(sermonsData);
      }
      if (usageResponse.ok) {
        const usageData = await usageResponse.json();
        setUsage(usageData.usage);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      return;
    }

    try {
      const response = await fetch(`${API}/checkout/status/${sessionId}`, {
        headers: { ...getAuthHeaders() }
      });
      
      if (response.ok) {
        const data = await response.json();

        // 'no_payment_required' is what a trial checkout reports — $0 is due
        // today, so Stripe never sets payment_status to 'paid' for it.
        if (data.payment_status === 'paid' || data.payment_status === 'no_payment_required') {
          toast.success(
            data.payment_status === 'no_payment_required'
              ? 'Your free trial has started!'
              : 'Payment successful! Your plan is now active.'
          );
          loadUserAndSermons();
          // Clear the URL params
          navigate('/dashboard', { replace: true });
          return;
        }
      }
      
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  };

  const startSyncProgress = (mode) => {
    const steps = mode === 'transcript'
      ? [
          { at: 0,  label: 'Preparing transcript...' },
          { at: 20, label: 'Analyzing with AI...' },
          { at: 60, label: 'Extracting insights...' },
          { at: 88, label: 'Almost done...' },
        ]
      : mode === 'audio-url'
      ? [
          { at: 0,  label: 'Downloading audio...' },
          { at: 20, label: 'Transcribing with Whisper...' },
          { at: 60, label: 'Analyzing with AI...' },
          { at: 88, label: 'Almost done...' },
        ]
      : [
          { at: 0,  label: 'Finding YouTube transcript...' },
          { at: 25, label: 'Analyzing with AI...' },
          { at: 60, label: 'Extracting insights...' },
          { at: 88, label: 'Almost done...' },
        ];

    let percent = 0;
    setSyncProgress({ label: steps[0].label, percent: 0 });

    const interval = setInterval(() => {
      percent = Math.min(percent + (Math.random() * 2.5 + 0.3), 96);
      const step = steps.slice().reverse().find(s => percent >= s.at);
      setSyncProgress({ label: step?.label || 'Processing...', percent: Math.round(percent) });
    }, 400);

    syncProgressRef.current = interval;
  };

  const stopSyncProgress = (success) => {
    if (syncProgressRef.current) {
      clearInterval(syncProgressRef.current);
      syncProgressRef.current = null;
    }
    if (success) {
      setSyncProgress({ label: 'Analysis complete!', percent: 100 });
    }
    setTimeout(() => setSyncProgress(null), 900);
  };

  const handleAnalyze = async (e) => {
    e.preventDefault();
    
    // Validate input based on mode
    if (inputMode === 'url' && !youtubeUrl.trim()) {
      toast.error('Please enter a YouTube URL');
      return;
    }

    if (inputMode === 'transcript' && !transcriptText.trim()) {
      toast.error('Please paste the sermon transcript');
      return;
    }

    if (inputMode === 'transcript' && transcriptText.trim().length < 100) {
      toast.error('Transcript is too short. Please paste the full sermon transcript.');
      return;
    }

    // If we already know the limit is hit, send them straight to pricing
    if (usage?.sermon_analysis && !usage.sermon_analysis.unlimited && usage.sermon_analysis.remaining === 0) {
      toast.error("You've used all 3 free analyses. Upgrade to continue.");
      navigate('/pricing');
      return;
    }

    setIsAnalyzing(true);

    const isYouTubeUrl = (url) => /youtube\.com|youtu\.be/i.test(url);
    const urlIsYouTube = inputMode === 'url' && isYouTubeUrl(youtubeUrl.trim());
    const urlIsDirectAudio = inputMode === 'url' && !urlIsYouTube;

    // Direct audio URLs always use the async path (download + transcription takes time)
    const isAsyncPath = urlIsDirectAudio;

    // Start simulated progress bar for the sync path
    if (!isAsyncPath) {
      startSyncProgress(inputMode);
    }

    try {
      const requestBody = inputMode === 'url'
        ? urlIsYouTube
          ? { youtube_url: youtubeUrl.trim() }
          : { audio_url: youtubeUrl.trim() }
        : { transcript_text: transcriptText, video_title: videoTitle || 'Sermon Analysis' };

      const endpoint = isAsyncPath
        ? `${API}/sermons/analyze-async`
        : `${API}/sermons/analyze`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const data = await response.json();

        if (data.job_id) {
          stopSyncProgress(false);
          toast.info('Analysis started! This may take a few minutes for longer audio...');
          pollJobStatus(data.job_id);
        } else {
          stopSyncProgress(true);
          setYoutubeUrl('');
          setTranscriptText('');
          setVideoTitle('');
          setAudioUrl('');
          setAudioUrlTitle('');
          setTimeout(() => {
            setIsAnalyzing(false);
            loadUserAndSermons();
            navigate(`/create/${data.analysis_id}`);
          }, 950);
        }
      } else {
        const error = await response.json();
        stopSyncProgress(false);
        if (response.status === 429) {
          setLimitHit(error.detail);
          toast.error("You've used all 3 free analyses. Upgrade to continue.");
          setTimeout(() => navigate('/pricing'), 1500);
        } else if (response.status === 403 && (error.detail || '').toLowerCase().includes('limit')) {
          setShowPaywall(true);
        } else if (inputMode === 'url') {
          setYoutubeError(error.detail || "We couldn't fetch this URL. The link may be private, inaccessible, or in an unsupported format.");
        } else {
          toast.error(error.detail || 'Failed to analyze sermon');
        }
        setIsAnalyzing(false);
      }
    } catch (error) {
      stopSyncProgress(false);
      if (inputMode === 'url') {
        setYoutubeError("Something went wrong fetching this URL. Please try a different link or paste the transcript instead.");
      } else {
        toast.error('Something went wrong. Please try again.');
      }
      setIsAnalyzing(false);
    }
  };
  
  const pollJobStatus = async (jobId) => {
    const pollInterval = 3000; // 3 seconds for more responsive UI
    const maxAttempts = 120; // 6 minutes max
    let attempts = 0;
    
    // Show the progress modal
    setShowProgress(true);
    
    const checkStatus = async () => {
      try {
        const response = await fetch(`${API}/sermons/job/${jobId}`, {
          headers: { ...getAuthHeaders() }
        });
        
        if (response.ok) {
          const job = await response.json();
          
          // Update the job status for the progress modal
          setJobStatus(job);
          
          if (job.status === 'completed' && job.analysis_id) {
            // Keep showing completion state briefly
            setTimeout(() => {
              setYoutubeUrl('');
              setAudioUrl('');
              setAudioUrlTitle('');
              setIsAnalyzing(false);
              setShowProgress(false);
              setJobStatus(null);
              loadUserAndSermons();
              navigate(`/create/${job.analysis_id}`);
            }, 1500);
            return;
          } else if (job.status === 'failed') {
            // Keep modal open to show error - user will close it
            setIsAnalyzing(false);
            return;
          }
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(checkStatus, pollInterval);
        } else {
          setIsAnalyzing(false);
          setJobStatus({ status: 'failed', message: 'Analysis is taking too long. Please check your library later.' });
        }
      } catch (error) {
        setIsAnalyzing(false);
        setJobStatus({ status: 'failed', message: 'Failed to check analysis status' });
      }
    };
    
    checkStatus();
  };

  const handleAudioUpload = async (e) => {
    e.preventDefault();

    if (!audioFile) {
      toast.error('Please select an audio file');
      return;
    }

    if (usage?.sermon_analysis && !usage.sermon_analysis.unlimited && usage.sermon_analysis.remaining === 0) {
      toast.error("You've used all 3 free analyses. Upgrade to continue.");
      navigate('/pricing');
      return;
    }

    setIsAnalyzing(true);
    
    try {
      const formData = new FormData();
      formData.append('file', audioFile);
      if (audioTitle) {
        formData.append('video_title', audioTitle);
      }
      
      const response = await fetch(`${API}/sermons/transcribe-audio`, {
        method: 'POST',
        headers: { ...getAuthHeaders() },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        setAudioFile(null);
        setAudioTitle('');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        loadUserAndSermons();
        navigate(`/create/${data.analysis_id}`);
      } else {
        const error = await response.json();
        if (response.status === 429) {
          toast.error("You've used all 3 free analyses. Upgrade to continue.");
          setTimeout(() => navigate('/pricing'), 1500);
        } else {
          toast.error(error.detail || 'Failed to transcribe audio');
        }
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 100MB)
      if (file.size > 100 * 1024 * 1024) {
        toast.error('File too large. Maximum size is 100MB.');
        return;
      }
      setAudioFile(file);
    }
  };

  const saveToHistory = (url, result) => {
    setUser((currentUser) => {
      if (!currentUser) return currentUser;
      const historyKey = `ss_search_history_${currentUser.user_id}`;
      const displayText = result.title || result.overview || result.summary || '';
      const label = extractHistoryLabel(url, displayText);
      const entry = {
        url,
        summary: result.summary || result.overview || '',
        source_type: result.source_type,
        label,
        timestamp: Date.now(),
      };
      setSearchHistory((prev) => {
        const deduped = prev.filter((h) => h.url !== url);
        const updated = [entry, ...deduped].slice(0, 30);
        try { localStorage.setItem(historyKey, JSON.stringify(updated)); } catch {}
        return updated;
      });
      return currentUser;
    });
  };

  const extractHistoryLabel = (url, summary) => {
    if (!summary) {
      try { return new URL(url).hostname.replace('www.', ''); } catch { return url.slice(0, 50); }
    }
    // Try to pull the first bold heading or first meaningful line from the summary
    const firstBold = summary.match(/\*\*([^*]{5,60})\*\*/);
    if (firstBold) return firstBold[1].trim();
    const firstLine = summary.split('\n').find((l) => l.trim().length > 10);
    if (firstLine) return firstLine.replace(/[*#]/g, '').trim().slice(0, 60);
    // Fallback: readable URL
    try { return new URL(url).hostname.replace('www.', ''); } catch { return url.slice(0, 50); }
  };

  const removeFromHistory = (url) => {
    setUser((currentUser) => {
      if (!currentUser) return currentUser;
      const historyKey = `ss_search_history_${currentUser.user_id}`;
      setSearchHistory((prev) => {
        const updated = prev.filter((h) => h.url !== url);
        try { localStorage.setItem(historyKey, JSON.stringify(updated)); } catch {}
        return updated;
      });
      return currentUser;
    });
  };

  const handleSummarize = async (e) => {
    e.preventDefault();
    const url = summaryUrl.trim();
    if (!url) {
      toast.error('Please paste a URL first');
      return;
    }
    setIsSummarizing(true);
    try {
      const response = await fetch(`${API}/summarize-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ url }),
      });
      if (response.ok) {
        const data = await response.json();
        saveToHistory(url, data);
        navigate('/url-summary', { state: data });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Could not summarise this URL. Please try another.');
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSummarizing(false);
    }
  };

  const handleDelete = async () => {
    if (!sermonToDelete) return;
    
    try {
      const response = await fetch(`${API}/sermons/${sermonToDelete}`, {
        method: 'DELETE',
        headers: { ...getAuthHeaders() }
      });
      
      if (response.ok) {
        setSermons(sermons.filter(s => s.analysis_id !== sermonToDelete));
        toast.success('Sermon deleted');
      } else {
        toast.error('Failed to delete sermon');
      }
    } catch (error) {
      toast.error('Something went wrong');
    } finally {
      setDeleteDialogOpen(false);
      setSermonToDelete(null);
    }
  };

  const handleLogout = () => logout(navigate);

  const handleProgressClose = () => {
    setShowProgress(false);
    setJobStatus(null);
    setIsAnalyzing(false);
    // Refresh sermons list in case analysis completed
    loadUserAndSermons();
  };

  const filteredSermons = sermons.filter(sermon => 
    sermon.video_title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    sermon.preacher?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    sermon.summary?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#fafaf9] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#1e3a8a] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fafaf9]" data-testid="dashboard-page">

      {/* Paywall Modal */}
      {showPaywall && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center animate-fade-in">

            {/* Usage indicator */}
            <div className="flex justify-center gap-2 mb-6">
              {[0, 1, 2].map((i) => (
                <div key={i} className="w-8 h-8 rounded-full bg-[#1e3a8a] flex items-center justify-center">
                  <span className="text-white text-xs font-bold">✓</span>
                </div>
              ))}
            </div>

            <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Crown className="w-7 h-7 text-amber-500" />
            </div>

            <h2 className="font-serif text-2xl font-bold text-[#1e3a8a] mb-2">
              You've used all 3 free analyses
            </h2>
            <p className="text-stone-500 text-sm mb-1">Your free allowance resets on the 1st of next month.</p>
            <p className="text-stone-600 mb-6 mt-3">
              Upgrade to <strong>Pastor Pro</strong> for unlimited sermon analyses, clean exports, home group guides, devotionals, and social posts — no limits, no watermarks.
            </p>

            {/* Plan highlight */}
            <div className="bg-[#1e3a8a]/5 border border-[#1e3a8a]/20 rounded-xl p-4 mb-6 text-left">
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold text-[#1e3a8a] text-sm">Pastor Pro Monthly</span>
                <span className="font-bold text-[#1e3a8a]">$9.99<span className="text-xs font-normal text-stone-500">/mo</span></span>
              </div>
              <p className="text-xs text-stone-500">Unlimited analyses · Clean exports · All tools included</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/pricing" className="flex-1" onClick={() => setShowPaywall(false)}>
                <Button className="btn-primary w-full">Upgrade Now</Button>
              </Link>
              <Button
                variant="ghost"
                className="flex-1 text-stone-400 text-sm"
                onClick={() => setShowPaywall(false)}
              >
                Maybe Later
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Header */}
      <header className="lg:hidden sticky top-0 z-50 glass border-b border-stone-200/50">
        <div className="flex items-center justify-between px-4 h-14">
          <button onClick={() => setSidebarOpen(true)} className="p-2" data-testid="mobile-sidebar-btn">
            <Menu className="w-5 h-5 text-stone-600" />
          </button>
          <span className="font-serif font-bold text-[#1e3a8a]">SermonSift</span>
          <div className="flex items-center gap-1">
            {user && !PAID_TIERS.includes(user.subscription_tier) && (
              <Link
                to="/pricing"
                className="text-xs font-semibold px-3 py-1 rounded-full bg-[#1e3a8a] text-white hover:bg-[#1e3a8a]/90 transition-colors"
              >
                Upgrade
              </Link>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="p-2">
                  <User className="w-5 h-5 text-stone-600" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="w-4 h-4 mr-2" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)}></div>
          <div className="absolute left-0 top-0 h-full w-72 bg-white shadow-xl animate-slide-up">
            <div className="p-4 border-b border-stone-200 flex items-center justify-between">
              <span className="font-serif font-bold text-[#1e3a8a]">SermonSift</span>
              <button onClick={() => setSidebarOpen(false)}>
                <X className="w-5 h-5 text-stone-600" />
              </button>
            </div>
            <nav className="p-4 space-y-1">
              <button className="sidebar-link active w-full">
                <LayoutDashboard className="w-5 h-5" strokeWidth={1.5} />
                Library
              </button>
              <Link to="/guide" className="sidebar-link w-full" onClick={() => setSidebarOpen(false)}>
                <BookMarked className="w-5 h-5" strokeWidth={1.5} />
                Home Group Guide
              </Link>
              <Link to="/devotional" className="sidebar-link w-full" onClick={() => setSidebarOpen(false)}>
                <BookOpen className="w-5 h-5" strokeWidth={1.5} />
                6-Day Devotional
              </Link>
              <Link to="/social" className="sidebar-link w-full" onClick={() => setSidebarOpen(false)}>
                <Wand2 className="w-5 h-5" strokeWidth={1.5} />
                Social Posts
              </Link>
            </nav>
          </div>
        </div>
      )}

      <div className="flex">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:flex flex-col w-64 min-h-screen bg-white border-r border-stone-200">
          <div className="p-6 border-b border-stone-200">
            <Link to="/" className="font-serif text-xl font-bold text-[#1e3a8a]">SermonSift</Link>
            {user && !PAID_TIERS.includes(user.subscription_tier) && (
              <Link
                to="/pricing"
                className="mt-3 flex items-center justify-center gap-2 w-full py-2 rounded-lg bg-[#1e3a8a] text-white text-sm font-semibold hover:bg-[#1e3a8a]/90 transition-colors"
              >
                <Crown className="w-4 h-4" />
                Upgrade to Pastor Pro
              </Link>
            )}
          </div>

          <nav className="p-4 space-y-1 border-b border-stone-100">
            <button className="sidebar-link active w-full">
              <LayoutDashboard className="w-5 h-5" strokeWidth={1.5} />
              Library
            </button>
            <Link to="/guide" className="sidebar-link w-full">
              <BookMarked className="w-5 h-5" strokeWidth={1.5} />
              Home Group Guide
            </Link>
            <Link to="/devotional" className="sidebar-link w-full">
              <BookOpen className="w-5 h-5" strokeWidth={1.5} />
              6-Day Devotional
            </Link>
            <Link to="/social" className="sidebar-link w-full">
              <Wand2 className="w-5 h-5" strokeWidth={1.5} />
              Social Posts
            </Link>
            <Link to="/blog" className="sidebar-link w-full">
              <BookOpen className="w-5 h-5" strokeWidth={1.5} />
              Blog
            </Link>
          </nav>

          {/* Search History */}
          <div className="flex-1 overflow-y-auto p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider">Research History</p>
              {searchHistory.length > 0 && (
                <button
                  onClick={() => {
                    if (!user) return;
                    const historyKey = `ss_search_history_${user.user_id}`;
                    localStorage.removeItem(historyKey);
                    setSearchHistory([]);
                  }}
                  className="text-xs text-stone-300 hover:text-stone-500 transition-colors"
                >
                  Clear all
                </button>
              )}
            </div>

            {searchHistory.length === 0 ? (
              <p className="text-xs text-stone-300 italic">No searches yet. Use the URL Summariser above to get started.</p>
            ) : (
              <div className="space-y-1">
                {searchHistory.map((item) => (
                  <div
                    key={item.url + item.timestamp}
                    className="group flex items-start gap-2 rounded-lg p-2 hover:bg-amber-50 cursor-pointer transition-colors"
                    onClick={() => { navigate('/url-summary', { state: { url: item.url, summary: item.summary, source_type: item.source_type, title: item.label } }); }}
                  >
                    <Youtube className="w-3.5 h-3.5 text-red-400 flex-shrink-0 mt-0.5" />
                    <span className="flex-1 text-xs text-stone-600 leading-snug line-clamp-2 group-hover:text-stone-800">
                      {item.label}
                    </span>
                    <button
                      onClick={(e) => { e.stopPropagation(); removeFromHistory(item.url); }}
                      className="opacity-0 group-hover:opacity-100 text-stone-300 hover:text-stone-500 flex-shrink-0 transition-opacity"
                      aria-label="Remove"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* User Profile Section */}
          <div className="p-4 border-t border-stone-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-[#1e3a8a] flex items-center justify-center text-white font-medium">
                {user?.name?.charAt(0) || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-stone-800 truncate capitalize">{user?.name}</p>
                <p className="text-sm text-stone-500 truncate">{user?.email}</p>
              </div>
            </div>
            
            {/* Subscription Status */}
            <div className="bg-stone-50 rounded-lg p-3 mb-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-stone-700">
                  {PAID_TIERS.includes(user?.subscription_tier) ? (
                    <span className="flex items-center gap-1">
                      <Crown className="w-4 h-4 text-amber-500" />
                      {user?.subscription_tier === 'founder_lifetime' ? 'Founder Lifetime'
                        : user?.subscription_tier === 'founder_monthly' ? 'Founder Monthly'
                        : user?.subscription_tier === 'pastor_pro' ? 'Pastor Pro'
                        : user?.subscription_tier === 'seminary_annual' ? 'Seminary'
                        : 'Pro'}
                    </span>
                  ) : (
                    'Free Plan'
                  )}
                </span>
                {!PAID_TIERS.includes(user?.subscription_tier) && (
                  <Link to="/pricing" className="text-xs text-[#0ea5e9] hover:underline">Upgrade</Link>
                )}
              </div>
              {PAID_TIERS.includes(user?.subscription_tier) && user?.subscription_tier !== 'founder_lifetime' && (
                <p className="text-xs text-stone-500 mt-2">
                  Need to cancel your plan or trial?{' '}
                  <a
                    href="mailto:jonathan@bethanylabs.com?subject=Cancel my SermonSift subscription"
                    className="text-[#0ea5e9] hover:underline"
                  >
                    Email us
                  </a>
                  {' '}and we'll take care of it.
                </p>
              )}
              {!PAID_TIERS.includes(user?.subscription_tier) && (
                <div className="mt-2">
                  <div className="progress-bar">
                    <div
                      className="progress-bar-fill"
                      style={{ width: `${(user?.monthly_analysis_count || 0) / 3 * 100}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-stone-500 mt-1">
                    {user?.monthly_analysis_count || 0}/3 analyses used this month
                  </p>
                </div>
              )}
            </div>
            
            <Button onClick={handleLogout} variant="ghost" className="w-full justify-start text-stone-600" data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" /> Sign Out
            </Button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 lg:p-8">
          <div className="max-w-5xl mx-auto">
            {/* Welcome Header */}
            <div className="mb-8 pb-6 border-b border-stone-200">
              <h1 className="font-serif text-2xl lg:text-3xl font-bold text-[#1e3a8a] mb-2">
                Welcome back, {user?.name?.split(' ')[0]?.replace(/^\w/, c => c.toUpperCase())}
              </h1>
              <p className="text-stone-500 text-sm leading-relaxed max-w-xl">
                Your ministry content engine — research smarter before you preach, and create more after you do.
              </p>
              {user?.is_early_adopter && (
                <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-full">
                  <Sparkles className="w-3 h-3" /> Early Adopter
                </div>
              )}
            </div>

            {/* URL Summariser — Pre-Study (navy) */}
            <div className="rounded-2xl border border-[#1e3a8a]/20 bg-gradient-to-br from-[#1e3a8a]/5 to-sky-50 p-6 mb-6 shadow-sm">
              <div className="flex items-center gap-2 mb-0.5">
                <div className="w-8 h-8 rounded-lg bg-[#1e3a8a]/10 flex items-center justify-center">
                  <Search className="w-4 h-4 text-[#1e3a8a]" strokeWidth={1.5} />
                </div>
                <h2 className="font-semibold text-lg text-[#1e3a8a]">URL Summariser</h2>
                <span className="text-xs bg-[#1e3a8a]/10 text-[#1e3a8a] px-2 py-0.5 rounded-full font-medium">Pre-Study</span>
              </div>
              <p className="text-sm text-[#1e3a8a]/70 mb-1 ml-10">Paste in a YouTube URL of content you're researching. It needs to be a YouTube link — nothing else will work here.</p>
              <p className="text-xs text-[#1e3a8a]/60 mb-4 ml-10">Works with videos that have captions enabled — the vast majority do (an estimated 94% of YouTube videos), so you should be covered.</p>
              <form onSubmit={handleSummarize} className="flex flex-col sm:flex-row gap-3">
                <Input
                  type="text"
                  placeholder="https://youtube.com/..."
                  value={summaryUrl}
                  onChange={(e) => { setSummaryUrl(e.target.value); }}
                  className="flex-1 border-[#1e3a8a]/20 bg-white/80 focus:border-[#1e3a8a] focus:ring-[#1e3a8a]/20 placeholder:text-stone-400"
                />
                <Button
                  type="submit"
                  className="whitespace-nowrap bg-[#1e3a8a] hover:bg-[#172554] text-white border-0 shadow-sm"
                  disabled={isSummarizing}
                >
                  {isSummarizing ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Summarising...
                    </span>
                  ) : 'Summarise'}
                </Button>
              </form>
            </div>

            {/* Sermon Upload — Post-Study (navy/indigo) */}
            <div className="rounded-2xl border border-[#1e3a8a]/20 bg-gradient-to-br from-[#1e3a8a]/5 to-indigo-50 p-6 mb-8 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-[#1e3a8a]/10 flex items-center justify-center">
                  <Upload className="w-4 h-4 text-[#1e3a8a]" strokeWidth={1.5} />
                </div>
                <h2 className="font-bold text-xl text-[#1e3a8a]">Insert Your Sermon Here</h2>
                <span className="text-xs bg-[#1e3a8a]/10 text-[#1e3a8a] px-2 py-0.5 rounded-full font-medium">Post-Study</span>
              </div>

              <Tabs value={inputMode} onValueChange={(v) => { setInputMode(v); setYoutubeError(null); }} className="w-full">
                <TabsList className={`grid w-full mb-4 ${user?.has_audio_access ? 'grid-cols-3' : 'grid-cols-2'}`}>
                  <TabsTrigger value="url" className="flex items-center gap-2">
                    <LinkIcon className="w-4 h-4" />
                    <span className="hidden sm:inline">Sermon</span> URL
                  </TabsTrigger>
                  <TabsTrigger value="transcript" className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    <span className="hidden sm:inline">Paste</span> Transcript
                  </TabsTrigger>
                  {user?.has_audio_access && (
                    <TabsTrigger value="audio" className="flex items-center gap-2">
                      <Upload className="w-4 h-4" />
                      <span className="hidden sm:inline">Upload</span> Audio
                    </TabsTrigger>
                  )}
                </TabsList>
                
                <form onSubmit={handleAnalyze} data-testid="analyze-form">
                  <TabsContent value="url" className="mt-0">
                    <div className="space-y-4">
                      <div className="flex flex-col sm:flex-row gap-4">
                        <Input
                          type="text"
                          placeholder="Paste a sermon URL (YouTube, MP3, podcast link...)"
                          value={youtubeUrl}
                          onChange={(e) => { setYoutubeUrl(e.target.value); setYoutubeError(null); }}
                          className="flex-1"
                          data-testid="dashboard-youtube-input"
                        />
                        <Button 
                          type="submit" 
                          className="btn-primary whitespace-nowrap"
                          disabled={isAnalyzing}
                          data-testid="dashboard-analyze-btn"
                        >
                          {isAnalyzing ? (
                            <span className="flex items-center gap-2">
                              <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                              Analyzing...
                            </span>
                          ) : (
                            'Analyze'
                          )}
                        </Button>
                      </div>
                      
                      <p className="text-xs text-stone-500">
                        YouTube links use existing captions. For MP3/podcast URLs, use the Upload Audio tab.
                      </p>

                      {youtubeError && (
                        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                          <div className="flex gap-3">
                            <Youtube className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-amber-800 mb-1">This link didn't work</p>
                              <p className="text-xs text-amber-700 mb-3">{youtubeError}</p>
                              <div className="flex flex-wrap gap-2">
                                <button
                                  type="button"
                                  onClick={() => { setYoutubeUrl(''); setYoutubeError(null); }}
                                  className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-white border border-amber-300 text-amber-800 hover:bg-amber-100 transition-colors"
                                >
                                  <LinkIcon className="w-3.5 h-3.5" /> Try a different link
                                </button>
                                <button
                                  type="button"
                                  onClick={() => { setInputMode('transcript'); setYoutubeError(null); }}
                                  className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-white border border-amber-300 text-amber-800 hover:bg-amber-100 transition-colors"
                                >
                                  <FileText className="w-3.5 h-3.5" /> Paste transcript
                                </button>
                                {user?.has_audio_access && (
                                  <button
                                    type="button"
                                    onClick={() => { setInputMode('audio'); setYoutubeError(null); }}
                                    className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-white border border-amber-300 text-amber-800 hover:bg-amber-100 transition-colors"
                                  >
                                    <Upload className="w-3.5 h-3.5" /> Upload audio
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="transcript" className="mt-0 space-y-4">
                    <Input
                      type="text"
                      placeholder="Sermon title (optional)"
                      value={videoTitle}
                      onChange={(e) => setVideoTitle(e.target.value)}
                      data-testid="transcript-title-input"
                    />
                    <Textarea
                      placeholder="Paste the sermon transcript here... (You can copy this from YouTube: click '...' below the video → 'Show transcript')"
                      value={transcriptText}
                      onChange={(e) => setTranscriptText(e.target.value)}
                      className="min-h-[150px]"
                      data-testid="transcript-text-input"
                    />
                    <Button
                      type="submit"
                      className="btn-primary w-full sm:w-auto"
                      disabled={isAnalyzing}
                      data-testid="dashboard-analyze-transcript-btn"
                    >
                      {isAnalyzing ? (
                        <span className="flex items-center gap-2">
                          <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                          Analyzing...
                        </span>
                      ) : (
                        'Analyze Transcript'
                      )}
                    </Button>
                  </TabsContent>

                </form>
                
                {/* Inline progress bar — shown during sync analysis */}
                {syncProgress && (
                  <div className="mt-4 p-4 bg-[#1e3a8a]/5 border border-[#1e3a8a]/20 rounded-xl animate-fade-in">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-stone-700 font-medium flex items-center gap-2">
                        {syncProgress.percent < 100 && (
                          <span className="w-3 h-3 border-2 border-[#1e3a8a] border-t-transparent rounded-full animate-spin inline-block" />
                        )}
                        {syncProgress.label}
                      </span>
                      <span className="text-sm font-bold text-[#1e3a8a]">{syncProgress.percent}%</span>
                    </div>
                    <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-[#1e3a8a] to-[#0ea5e9] rounded-full transition-all duration-500 ease-out"
                        style={{ width: `${syncProgress.percent}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Usage counter */}
                {!syncProgress && usage?.sermon_analysis && (inputMode === 'url' || inputMode === 'transcript') && (() => {
                  const u = usage.sermon_analysis;
                  if (u.unlimited) {
                    return (
                      <div className="flex items-center justify-end mt-2">
                        <span className="text-xs text-stone-400">Unlimited analyses</span>
                      </div>
                    );
                  }
                  const pct = (u.used / u.limit) * 100;
                  return (
                    <div className="mt-4 p-3 bg-stone-50 border border-stone-200 rounded-xl">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className={`text-xs font-semibold ${u.remaining === 0 ? 'text-red-600' : u.remaining <= 2 ? 'text-amber-600' : 'text-stone-700'}`}>
                          {u.remaining === 0
                            ? 'No analyses remaining this month'
                            : `${u.remaining} of ${u.limit} ${u.remaining === 1 ? 'analysis' : 'analyses'} remaining this month`}
                        </span>
                        {u.remaining <= 2 && (
                          <Link to="/pricing" className="text-xs text-[#0ea5e9] hover:underline font-medium">
                            View Plans →
                          </Link>
                        )}
                      </div>
                      <div className="h-1.5 bg-stone-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${pct >= 100 ? 'bg-red-500' : pct >= 60 ? 'bg-amber-500' : 'bg-[#1e3a8a]'}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })()}

                {/* Inline limit alert (429) */}
                {limitHit && (
                  <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-4 text-center">
                    <p className="text-sm font-semibold text-red-800 mb-1">Monthly limit reached</p>
                    <p className="text-xs text-red-700 mb-3">{limitHit}</p>
                    <Link to="/pricing">
                      <button className="text-xs font-semibold px-4 py-2 rounded-lg bg-[#1e3a8a] text-white">
                        Upgrade Plan
                      </button>
                    </Link>
                  </div>
                )}

                {/* Audio Upload Tab */}
                {user?.has_audio_access && (
                  <TabsContent value="audio" className="mt-0">
                    <form onSubmit={handleAudioUpload} className="space-y-4">
                      <Input
                        type="text"
                        placeholder="Sermon title (optional)"
                        value={audioTitle}
                        onChange={(e) => setAudioTitle(e.target.value)}
                        data-testid="audio-title-input"
                      />
                      
                      <div 
                        className="border-2 border-dashed border-stone-300 rounded-xl p-8 text-center hover:border-[#0ea5e9] transition-colors cursor-pointer"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="audio/*,video/mp4,video/webm"
                          onChange={handleFileChange}
                          className="hidden"
                          data-testid="audio-file-input"
                        />
                        {audioFile ? (
                          <div className="flex flex-col items-center gap-2">
                            <Mic className="w-10 h-10 text-[#0ea5e9]" />
                            <p className="font-medium text-stone-800">{audioFile.name}</p>
                            <p className="text-sm text-stone-500">
                              {(audioFile.size / (1024 * 1024)).toFixed(1)} MB
                            </p>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setAudioFile(null);
                                if (fileInputRef.current) fileInputRef.current.value = '';
                              }}
                            >
                              Remove
                            </Button>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center gap-2">
                            <Upload className="w-10 h-10 text-stone-400" />
                            <p className="font-medium text-stone-600">Drop audio/video file here</p>
                            <p className="text-sm text-stone-500">or click to browse</p>
                            <p className="text-xs text-stone-400 mt-2">Supports MP3, WAV, M4A, MP4, WebM (max 100MB)</p>
                          </div>
                        )}
                      </div>
                      
                      <Button 
                        type="submit" 
                        className="btn-primary w-full"
                        disabled={isAnalyzing || !audioFile}
                        data-testid="dashboard-analyze-audio-btn"
                      >
                        {isAnalyzing ? (
                          <span className="flex items-center gap-2">
                            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                            Transcribing & Analyzing...
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            <Mic className="w-4 h-4" />
                            Transcribe & Analyze
                          </span>
                        )}
                      </Button>
                    </form>
                  </TabsContent>
                )}
              </Tabs>

              {/* What one sermon can produce — shown below the form */}
              <div className="mt-6 pt-6 border-t border-[#1e3a8a]/10">
                  <p className="text-xs font-semibold text-[#1e3a8a]/50 uppercase tracking-wider mb-3">From one sermon, you can create all of this:</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {[
                      { label: 'Full Analysis', desc: 'Themes, key quotes & scripture', bg: 'bg-[#1e3a8a]', icon: '🔍' },
                      { label: 'Home Group Guide', desc: 'Discussion guide with questions', bg: 'bg-teal-500', icon: '📚' },
                      { label: '6-Day Devotional', desc: 'A full week of daily readings', bg: 'bg-amber-500', icon: '📖' },
                      { label: 'Social Posts', desc: '20 posts for X / Twitter', bg: 'bg-[#0ea5e9]', icon: '🐦' },
                      { label: 'Sermon eBook', desc: 'Long-form booklet from your message', bg: 'bg-violet-600', icon: '📝' },
                      { label: 'Instagram Captions', desc: 'Captions ready to post', bg: 'bg-rose-500', icon: '📸', soon: true },
                      { label: 'LinkedIn Posts', desc: 'Posts for ministry leaders', bg: 'bg-blue-600', icon: '💼', soon: true },
                      { label: 'Email Newsletter', desc: 'Pastoral email to your congregation', bg: 'bg-emerald-600', icon: '✉️' },
                      { label: 'Study Notes', desc: 'Structured teaching notes', bg: 'bg-orange-500', icon: '📋' },
                    ].map((item) => (
                      <div key={item.label} className={`relative rounded-xl p-3 ${item.bg} ${item.soon ? 'opacity-50' : ''}`}>
                        {item.soon && (
                          <span className="absolute top-2 right-2 text-[10px] bg-white/20 text-white px-1.5 py-0.5 rounded font-medium leading-none">Soon</span>
                        )}
                        <div className="text-xl mb-1.5">{item.icon}</div>
                        <p className="font-semibold text-sm leading-tight text-white">{item.label}</p>
                        <p className="text-xs mt-0.5 leading-snug text-white opacity-80">{item.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
            </div>

            {/* Quick Tools */}
            <div className="mb-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Link
                to="/guide"
                className="group flex flex-col gap-4 p-5 rounded-2xl bg-white border border-stone-200 hover:border-[#1e3a8a] hover:shadow-lg transition-all duration-200"
              >
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 rounded-xl bg-[#1e3a8a]/10 flex items-center justify-center">
                    <BookMarked className="w-5 h-5 text-[#1e3a8a]" strokeWidth={1.5} />
                  </div>
                  <ChevronRight className="w-4 h-4 text-stone-300 group-hover:text-[#1e3a8a] transition-colors" />
                </div>
                <div>
                  <p className="font-serif font-bold text-[#1e3a8a] mb-1">Home Group Guide</p>
                  <p className="text-sm text-stone-500 leading-snug">Turn any sermon into a small group discussion guide.</p>
                </div>
              </Link>

              <Link
                to="/devotional"
                className="group flex flex-col gap-4 p-5 rounded-2xl bg-white border border-stone-200 hover:border-amber-500 hover:shadow-lg transition-all duration-200"
              >
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-amber-600" strokeWidth={1.5} />
                  </div>
                  <ChevronRight className="w-4 h-4 text-stone-300 group-hover:text-amber-500 transition-colors" />
                </div>
                <div>
                  <p className="font-serif font-bold text-[#1f2937] mb-1">6-Day Devotional</p>
                  <p className="text-sm text-stone-500 leading-snug">Generate a week-long devotional series from a sermon.</p>
                </div>
              </Link>

              <Link
                to="/social"
                className="group flex flex-col gap-4 p-5 rounded-2xl bg-white border border-stone-200 hover:border-[#0ea5e9] hover:shadow-lg transition-all duration-200"
              >
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center">
                    <Wand2 className="w-5 h-5 text-[#0ea5e9]" strokeWidth={1.5} />
                  </div>
                  <ChevronRight className="w-4 h-4 text-stone-300 group-hover:text-[#0ea5e9] transition-colors" />
                </div>
                <div>
                  <p className="font-serif font-bold text-[#1f2937] mb-1">Social Posts</p>
                  <p className="text-sm text-stone-500 leading-snug">20 ready-to-post quotes and takeaways for X / Twitter.</p>
                </div>
              </Link>

              <Link
                to="/ebook"
                className="group flex flex-col gap-4 p-5 rounded-2xl bg-white border border-stone-200 hover:border-violet-500 hover:shadow-lg transition-all duration-200"
              >
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-violet-600" strokeWidth={1.5} />
                  </div>
                  <ChevronRight className="w-4 h-4 text-stone-300 group-hover:text-violet-500 transition-colors" />
                </div>
                <div>
                  <p className="font-serif font-bold text-[#1f2937] mb-1">Sermon Ebook</p>
                  <p className="text-sm text-stone-500 leading-snug">Turn sermon notes into a polished, downloadable ebook.</p>
                </div>
              </Link>
            </div>

            {/* Saved Sermons */}
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <h2 className="font-semibold text-lg text-stone-800 flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-[#0ea5e9]" strokeWidth={1.5} />
                  Your Library
                  <span className="text-stone-400 font-normal text-sm">({sermons.length})</span>
                </h2>
                
                {sermons.length > 0 && (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <Input
                      type="text"
                      placeholder="Search sermons..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 w-full sm:w-64"
                      data-testid="search-sermons-input"
                    />
                  </div>
                )}
              </div>

              {sermons.length === 0 ? (
                <div className="empty-state">
                  <BookOpen className="empty-state-icon" strokeWidth={1} />
                  <h3 className="empty-state-title">No sermons yet</h3>
                  <p className="empty-state-description">
                    Paste a YouTube URL above to analyze your first sermon and start building your library.
                  </p>
                </div>
              ) : filteredSermons.length === 0 ? (
                <div className="text-center py-8 text-stone-500">
                  No sermons matching "{searchQuery}"
                </div>
              ) : (
                <div className="grid gap-4">
                  {filteredSermons.map((sermon) => (
                    <div 
                      key={sermon.analysis_id} 
                      className="card card-interactive p-5 group"
                      data-testid={`sermon-card-${sermon.analysis_id}`}
                    >
                      <div className="flex items-start gap-4">
                        {/* Thumbnail placeholder */}
                        <div className="hidden sm:flex w-24 h-16 bg-stone-100 rounded-lg items-center justify-center flex-shrink-0">
                          <BookOpen className="w-8 h-8 text-stone-300" strokeWidth={1} />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <Link 
                            to={`/results/${sermon.analysis_id}`}
                            className="font-semibold text-stone-800 hover:text-[#1e3a8a] line-clamp-1 block"
                          >
                            {sermon.video_title || 'Untitled Sermon'}
                          </Link>
                          <p className="text-sm text-stone-500 mt-1">
                            {sermon.preacher && sermon.preacher !== 'Unknown' && (
                              <span>{sermon.preacher} • </span>
                            )}
                            <span className="flex items-center gap-1 inline-flex">
                              <Calendar className="w-3 h-3" />
                              {formatDate(sermon.created_at)}
                            </span>
                          </p>
                          <p className="text-sm text-stone-600 mt-2 line-clamp-2">
                            {sermon.summary?.substring(0, 150)}...
                          </p>
                          
                          {/* Tags */}
                          <div className="flex flex-wrap gap-2 mt-3">
                            {sermon.key_themes?.slice(0, 3).map((theme, i) => (
                              <span key={i} className="text-xs bg-stone-100 text-stone-600 px-2 py-1 rounded">
                                {theme}
                              </span>
                            ))}
                            {sermon.scripture_references?.length > 0 && (
                              <span className="scripture-badge text-xs">
                                {sermon.scripture_references.length} Scripture{sermon.scripture_references.length !== 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Link 
                            to={`/results/${sermon.analysis_id}`}
                            className="p-2 text-stone-400 hover:text-[#1e3a8a] hover:bg-stone-100 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <ChevronRight className="w-5 h-5" />
                          </Link>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              setSermonToDelete(sermon.analysis_id);
                              setDeleteDialogOpen(true);
                            }}
                            className="p-2 text-stone-400 hover:text-red-500 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                            data-testid={`delete-sermon-${sermon.analysis_id}`}
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete sermon analysis?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete this sermon analysis from your library.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Analysis Progress Modal */}
      {showProgress && (
        <AnalysisProgress 
          jobStatus={jobStatus} 
          onClose={handleProgressClose}
        />
      )}
    </div>
  );
}
